import { useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Button } from '@/components/primitives/Button';
import { Eyebrow } from '@/components/domain/Eyebrow';
import { useFadeUpOnScroll } from '@/hooks/useFadeUpOnScroll';
import styles from './AuditCompleteSection.module.css';

export const AuditCompleteSection: FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useFadeUpOnScroll(ref, { selector: '[data-fade]', stagger: 0.1 });

  const results = t('home.auditComplete.results', { returnObjects: true }) as string[];

  return (
    <Section tone="default" id="audit-complete">
      <Container size="default">
        <div ref={ref}>
          <header className={styles.header}>
            <div data-fade>
              <Eyebrow>{t('home.auditComplete.kicker')}</Eyebrow>
            </div>
            <h2 className={styles.title} data-fade>
              {t('home.auditComplete.title')}
            </h2>
          </header>

          <div className={styles.card} data-fade>
            <p className={styles.confirmation}>{t('home.auditComplete.confirmation')}</p>
            <ul className={styles.results}>
              {results.map((r) => (
                <li key={r} className={styles.result}>
                  <span className={styles.checkIcon} aria-hidden="true">
                    ✓
                  </span>
                  <span className={styles.resultText}>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.timing} data-fade>
            <div className={styles.timingBefore}>
              <p>{t('home.auditComplete.before')}</p>
            </div>
            <div className={styles.timingArrow} aria-hidden="true">
              →
            </div>
            <div className={styles.timingAfter}>
              <p className={styles.timingValue}>{t('home.auditComplete.after')}</p>
            </div>
          </div>

          <p className={styles.footnote} data-fade>
            {t('home.auditComplete.footnote')}
          </p>
          <p className={styles.quote} data-fade>
            {t('home.auditComplete.quote')}
          </p>
          <p className={styles.boundary} data-fade>
            {t('home.auditComplete.boundary')}
          </p>

          <div className={styles.actions} data-fade>
            <Button as="link" to="/" size="lg" variant="primary">
              {t('home.auditComplete.backToHome')}
            </Button>
          </div>
        </div>
      </Container>
    </Section>
  );
};

AuditCompleteSection.displayName = 'AuditCompleteSection';
